/**
 * three.js scene: the octagon cage, procedural fighters posed from the
 * simulation's skeleton, an over-the-shoulder camera driven by the controls.
 */
import { eyeLocal, poseFighter, rotateY, type Vec3 } from "@meleeroyale/sim";
import * as THREE from "three";
import type { Controls } from "../input/controls.ts";
import type { ClientView, RenderFighter } from "../net/game-client.ts";
import { cameraRig } from "./camera-rig.ts";
import { DamageNumbers, type DamagePop } from "./damage-numbers.ts";
import { FighterMesh } from "./fighter-mesh.ts";
import { type HitFlashEvent, HitFlashes } from "./hit-flash.ts";
import { accentFor } from "./palette.ts";

const WALL_HEIGHT = 3.2;

export class Arena {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly cage = new THREE.Group();
  private readonly ring: THREE.Mesh;
  private readonly ringMaterial: THREE.MeshBasicMaterial;
  private readonly fighters = new Map<number, FighterMesh>();
  private readonly flashes: HitFlashes;
  private readonly damage: DamageNumbers;
  private fogSet = false;
  private lastView: ClientView | null = null;
  private readonly canvas: HTMLCanvasElement;
  private readonly controls: Controls;
  private frame = 0;
  private running = false;
  private readonly resizeObserver: ResizeObserver;
  private viewSource: (now: number) => ClientView | null = () => null;

  /** `damageLayer`: an empty element over the canvas that the damage numbers are drawn in. */
  constructor(canvas: HTMLCanvasElement, controls: Controls, damageLayer: HTMLElement) {
    this.canvas = canvas;
    this.controls = controls;
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.scene.background = new THREE.Color(0x0b0d10);
    this.camera = new THREE.PerspectiveCamera(70, 1, 0.05, 200);

    const hemi = new THREE.HemisphereLight(0xbfd4ff, 0x1a1410, 0.55);
    const key = new THREE.DirectionalLight(0xffc98a, 2.2);
    key.position.set(6, 12, 4);
    const rim = new THREE.DirectionalLight(0x4cc9f0, 0.5);
    rim.position.set(-8, 6, -6);
    this.scene.add(hemi, key, rim);

    // Floor and cage at unit apothem, scaled per frame to the live radius.
    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(1 / Math.cos(Math.PI / 8), 8),
      new THREE.MeshStandardMaterial({ color: 0x2a2d33, roughness: 0.95, metalness: 0.05 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.rotation.z = Math.PI / 8;
    this.cage.add(floor);
    const wallMaterial = new THREE.MeshBasicMaterial({
      color: 0x8a94a6,
      transparent: true,
      opacity: 0.08,
      side: THREE.DoubleSide,
    });
    const barMaterial = new THREE.LineBasicMaterial({
      color: 0xa9b3c4,
      transparent: true,
      opacity: 0.55,
    });
    const sideLength = 2 * Math.tan(Math.PI / 8);
    for (let i = 0; i < 8; i += 1) {
      const angle = (i / 8) * Math.PI * 2;
      const wall = new THREE.Group();
      const plane = new THREE.Mesh(new THREE.PlaneGeometry(sideLength, WALL_HEIGHT), wallMaterial);
      plane.position.y = WALL_HEIGHT / 2;
      wall.add(plane);
      const points: THREE.Vector3[] = [];
      const bars = 6;
      for (let b = 0; b <= bars; b += 1) {
        const x = -sideLength / 2 + (sideLength * b) / bars;
        points.push(new THREE.Vector3(x, 0, 0), new THREE.Vector3(x, WALL_HEIGHT, 0));
      }
      for (let h = 0; h <= 3; h += 1) {
        const y = (WALL_HEIGHT * h) / 3;
        points.push(
          new THREE.Vector3(-sideLength / 2, y, 0),
          new THREE.Vector3(sideLength / 2, y, 0),
        );
      }
      wall.add(
        new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(points), barMaterial),
      );
      wall.position.set(Math.sin(angle), 0, Math.cos(angle));
      wall.rotation.y = angle;
      this.cage.add(wall);
    }
    this.ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xffa42b,
      transparent: true,
      opacity: 0,
    });
    this.ring = new THREE.Mesh(new THREE.RingGeometry(0.93, 1, 8), this.ringMaterial);
    this.ring.rotation.x = -Math.PI / 2;
    this.ring.rotation.z = Math.PI / 8;
    this.ring.position.y = 0.02;
    this.cage.add(this.ring);
    this.scene.add(this.cage);
    this.flashes = new HitFlashes(this.scene);
    this.damage = new DamageNumbers(damageLayer);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();
  }

  start(viewSource: (now: number) => ClientView | null): void {
    this.viewSource = viewSource;
    this.running = true;
    const tick = (now: number) => {
      if (!this.running) return;
      this.render(now);
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }

  dispose(): void {
    this.running = false;
    cancelAnimationFrame(this.frame);
    this.resizeObserver.disconnect();
    for (const f of this.fighters.values()) f.dispose();
    this.flashes.dispose();
    this.damage.dispose();
    this.renderer.dispose();
  }

  /** Lights up the spot a strike met a body; remote hits wait for the interpolated view. */
  flashHit(hit: HitFlashEvent, delayMs: number): void {
    this.flashes.spawn(hit, performance.now(), delayMs);
  }

  /** Shows a damage figure where a hit landed, on the same clock as its flash. */
  popDamage(pop: DamagePop, delayMs: number): void {
    this.damage.spawn(pop, performance.now(), delayMs);
  }

  /** World position of a point given in a fighter's local frame, from the last drawn pose. */
  worldPoint(fighterId: number, local: Vec3): Vec3 | null {
    const f = this.lastView?.fighters.find((x) => x.id === fighterId);
    return f ? HitFlashes.world(f, local) : null;
  }

  private resize(): void {
    const w = this.canvas.clientWidth || 1;
    const h = this.canvas.clientHeight || 1;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  private render(now: number): void {
    const view = this.viewSource(now);
    if (!view) return;
    this.lastView = view;
    if (!this.fogSet) {
      // The cage starts at its full size, which depends on how many fighters are in.
      this.scene.fog = new THREE.Fog(0x0b0d10, view.cage.r * 2.5, view.cage.r * 6);
      this.fogSet = true;
    }
    const s = view.cage.r;
    this.cage.scale.set(s, 1, s);
    const pulse = view.cage.warning ? 0.35 + 0.35 * Math.abs(Math.sin(now / 120)) : 0;
    this.ringMaterial.opacity = pulse;

    for (const f of view.fighters) this.updateFighter(f);
    const poseOf = (id: number) => view.fighters.find((f) => f.id === id) ?? null;
    this.flashes.update(now, poseOf);
    this.updateCamera(view);
    this.renderer.render(this.scene, this.camera);
    this.damage.update(now, this.camera, poseOf);
  }

  private updateFighter(f: RenderFighter): void {
    let mesh = this.fighters.get(f.id);
    if (!mesh) {
      mesh = new FighterMesh(accentFor(f.id), f.nickname);
      this.fighters.set(f.id, mesh);
      this.scene.add(mesh.group);
      mesh.setNickname(f.nickname, accentFor(f.id));
    }
    mesh.pose(poseFighter(f), f.alive, f.guard, f.exhausted, f.telegraph);
  }

  private updateCamera(view: ClientView): void {
    const me = view.fighters[view.myId] ?? view.fighters[0];
    if (!me) return;
    // Pivot on the eye, so ducking and slipping move the view the way the head moved.
    const eye = rotateY(eyeLocal(me.duck, me.lean), Math.sin(me.facing), Math.cos(me.facing));
    const rig = cameraRig(
      { x: me.pos.x + eye.x, y: me.pos.y + eye.y, z: me.pos.z + eye.z },
      this.controls.yaw,
      (this.controls.pitch * Math.PI) / 180,
      me.alive,
    );
    this.camera.position.set(rig.position.x, rig.position.y, rig.position.z);
    this.camera.lookAt(rig.focus.x, rig.focus.y, rig.focus.z);
  }
}
